import React, {useEffect, useState} from "react";
import {ImageBackground, Keyboard, ScrollView, StyleSheet, TextInput, TouchableWithoutFeedback, useWindowDimensions, View} from "react-native";
import {Box, HStack, Icon, Text, VStack} from "native-base";
import {Ionicons} from "@expo/vector-icons";
import {CartItem, MenuListData, OptionItemData, OptionListData} from "./lib/model";
import apps, {Happyhour, SaleMode} from "./lib/apps";
import {PALETTE} from "./lib/palette";
import {FLAG_STYLES, FlagKey} from "./lib/flags";
import {useRecoilState, useSetRecoilState} from "recoil";
import {cartState} from "./state/cartState";
import {lastCartTouchState} from "./state/idleState";
import {useNavigation} from "@react-navigation/native";
import {menuUri, money} from "./lib/utils";
import {useScrollMore, ScrollMoreHint} from "./component/ScrollMore";
import {BackButton, CIRCLE_BTN} from "./component/BackButton";
import {showMessage} from "react-native-flash-message";

function Price({saleMode, menu}: { saleMode: SaleMode, menu: MenuListData }) {
    if (apps.isSale(saleMode, menu)) {
        // 리스트(shop.tsx Badge)와 동일: happyhour 전용 할인 항목은 HAPPY(보라), 그 외는 SALE(빨강).
        const isHappy = menu.happy1Only || menu.happy2Only;
        return (
            <HStack alignItems="center">
                <Box style={[styles.saleBadge, isHappy && {backgroundColor: "#7a3ff2"}]}>
                    <Text style={styles.saleBadgeText}>{isHappy ? "HAPPY" : "SALE"}</Text>
                </Box>
                <Text style={styles.menuPriceStroked}>{money(menu.price)}</Text>
                <Text style={styles.specialPrice}>{money(apps.getPrice(saleMode, menu))}</Text>
            </HStack>
        );
    }

    return (
        <Text style={styles.menuPrice}>{money(menu.price)}</Text>
    );
}

// 상세화면 칩 태그(Veg/GF/Spicy). 색/라벨은 lib/flags 공용, 칩 크기만 상세용으로 약간 크게(styles.flagChip).
function MenuFlags({menu}: { menu: MenuListData }) {
    const on = [] as Array<FlagKey>;
    if (menu.vegetarian) on.push("veg");
    if (menu.gluetenFree) on.push("gf");   // 기존 오타 필드명 유지
    if (menu.spicy) on.push("spicy");
    if (!on.length && !menu.popular) return null;
    return (
        <HStack style={[styles.flagRow, {alignItems: "center"}]} space="1.5">
            {on.map((k) =>
                <Box key={k} style={[styles.flagChip, {backgroundColor: FLAG_STYLES[k].bg}]}>
                    <Text style={[styles.flagText, {color: FLAG_STYLES[k].color}]}>{FLAG_STYLES[k].label}</Text>
                </Box>
            )}
            {/* POPULAR(최근 주문 top10) — 식이 플래그 뒤 맨 마지막, 🔥 아이콘만 (리스트와 동일). */}
            {menu.popular && <Text style={{fontSize: 18}}>🔥</Text>}
        </HStack>
    );
}

function MenuBase({saleMode, menu}: { saleMode: SaleMode, menu: MenuListData }) {
    return (
        <>
            <VStack style={styles.menuBaseContainer}>
                <MenuFlags menu={menu}/>
                <HStack style={styles.menuNameRow}>
                    <Text style={styles.menuName}>{menu.name}</Text>
                </HStack>
                <HStack style={styles.menuPriceRow}>
                    <Price saleMode={saleMode} menu={menu}/>
                </HStack>
                <Text style={styles.menuDescr}>{menu.descr}</Text>
            </VStack>
            <Box style={styles.legend}>
                <Text style={styles.legendTitle}>🐟 Seafood Origin</Text>
                <Text style={styles.legendDescr}>(A) Australian · (I) Imported · (M) Mixed</Text>
            </Box>
        </>
    );
}

function MenuImage({menuUri, goBack, isNew, heroH}: { menuUri: { uri: string }, goBack: () => void, isNew?: boolean, heroH: number }) {
    return <ImageBackground source={menuUri} style={[styles.hero, {height: heroH}]} resizeMode="cover">
        <BackButton onPress={goBack} containerStyle={[CIRCLE_BTN, styles.backBtnPos]} size={12} thickness={2.5} />
        {/* NEW 배지 — 리스트(shop.tsx Badge NEW)와 동일 룩(초록). 좌상단은 back 버튼이라 우상단. */}
        {isNew && (
            <Box style={styles.heroNewBadge}><Text style={styles.heroNewText}>NEW</Text></Box>
        )}
    </ImageBackground>;
}

function MenuOption({option, optionSelected, onUpdate}:
                        {
                            option: OptionListData, optionSelected: OptionItemData[],
                            onUpdate: (option: OptionListData, optionItems: OptionItemData[]) => void
                        }) {
    const accent = apps.getAccent();
    const [selected, setSelected] = useState<OptionItemData[]>(optionSelected == undefined ? [] : optionSelected);

    function selectSingle(optionItem: OptionItemData) {
        const sel = [optionItem];
        onUpdate(option, sel)
        setSelected(sel);
    }

    function selectMultiple(optionItem: OptionItemData) {
        let sel = []
        if (selected.find(o => optionItem.optionItemId == o.optionItemId)) {
            sel = selected.filter(o => optionItem.optionItemId != o.optionItemId);
        } else {
            sel = [...selected, optionItem];
        }

        const isValidSelection = sel.length >= option.min && sel.length <= option.max;
        // Display error message if the selection is not valid
        if (!isValidSelection) {
            showMessage({
                message: "Please select between " + option.min + " and " + option.max + " items for " + option.descr,
                type: "danger",
                duration: 1000
            });
        } else {
            onUpdate(option, sel)
            setSelected(sel);
            //console.log(optionItem);
            //console.log(sel);
        }

    }

    function resetSelect() {
        setSelected([]);
        onUpdate(option, [])
    }

    const onAccent = apps.onAccent();

    function renderRow(item: OptionItemData, index: number) {
        const on = selected.includes(item);
        const markStyle = [
            styles.mark,
            option.multiple ? styles.markCheck : styles.markRadio,
            on && {backgroundColor: accent, borderColor: accent},
        ];
        const inner = (
            <HStack style={styles.opt}>
                <View style={markStyle}>
                    {on && <Text style={[styles.markGlyph, {color: onAccent}]}>{option.multiple ? "✓" : "●"}</Text>}
                </View>
                <Text style={styles.optLabel}>{item.name}</Text>
                {item.price != 0 &&
                    <Text style={styles.optPrice}>{item.price > 0 ? '+' : '-'}{money(Math.abs(item.price))}</Text>
                }
            </HStack>
        );

        // single-select: an already-selected radio is not tappable (matches prior behavior)
        if (!option.multiple && on) {
            return <View key={index}>{inner}</View>;
        }
        return (
            <TouchableWithoutFeedback key={index} onPress={() => {
                option.multiple ? selectMultiple(item) : selectSingle(item);
            }}>
                {inner}
            </TouchableWithoutFeedback>
        );
    }

    return <View style={styles.og}>
        <HStack style={styles.ogHead}>
            <Text style={styles.ogTitle}>{option.descr}</Text>

            {option.optional ?
                <TouchableWithoutFeedback onPress={() => resetSelect()}>
                    <View style={styles.reqPillOptional}>
                        <Text style={styles.reqPillOptionalText}>Optional</Text>
                    </View>
                </TouchableWithoutFeedback>
                :
                <View style={[styles.reqPill, {backgroundColor: apps.accentTint()}]}>
                    <Text style={[styles.reqPillText, {color: accent}]}>Required</Text>
                </View>}
        </HStack>

        {option.items.map((item, index) => renderRow(item, index))}
    </View>;
}

function OptionList({options, optionsSelected, onUpdate}: {
    options: OptionListData[],
    optionsSelected: Map<OptionListData, OptionItemData[]>,
    onUpdate: (option: OptionListData, optionItems: OptionItemData[]) => void
}) {
    return (
        <Box style={{flex: 1, backgroundColor: PALETTE.SURFACE, width: "100%"}}>
            {options.map((option: OptionListData, index) =>
                <MenuOption key={index} option={option} optionSelected={optionsSelected.get(option)}
                            onUpdate={onUpdate}/>
            )}
        </Box>
    );
}

const Stepper: React.FC<{ qty: number, onQtyUpdated: (qty: number) => void }> = ({qty, onQtyUpdated}) => {
    return (
        <HStack style={styles.stepper}>
            <TouchableWithoutFeedback onPress={() => onQtyUpdated(qty > 1 ? qty - 1 : 1)}>
                <View style={styles.stepBtn}><Text style={styles.stepBtnGlyph}>−</Text></View>
            </TouchableWithoutFeedback>
            <Text style={styles.stepQty}>{qty}</Text>
            <TouchableWithoutFeedback onPress={() => onQtyUpdated(qty < 49 ? qty + 1 : 49)}>
                <View style={styles.stepBtn}><Text style={styles.stepBtnGlyph}>+</Text></View>
            </TouchableWithoutFeedback>
        </HStack>
    );
};

// 항목별 메모 입력(선택). shop.enableItemNote가 true일 때만 렌더(Menu에서 게이팅).
const NoteInput: React.FC<{ note: string, onChangeNote: (note: string) => void }> = ({note, onChangeNote}) => {
    return (
        <VStack style={styles.noteWrap}>
            <Text style={styles.noteLabel}>Note (optional)</Text>
            <TextInput
                style={styles.noteInput}
                multiline
                numberOfLines={2}
                maxLength={255}
                placeholder=""
                placeholderTextColor={PALETTE.MUTED}
                value={note}
                onChangeText={onChangeNote}
            />
        </VStack>
    );
};

const AddButton: React.FC<{
    menu: MenuListData, onAdd: () => void, total: number, qty: number,
    onQtyUpdated: (qty: number) => void
}> = ({menu, onAdd, total, qty, onQtyUpdated}) => {
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();
    // 좁은 모바일 폭에선 라벨/금액 폰트를 줄여 한 줄에 온전히 담는다(잘림 방지).
    const {width} = useWindowDimensions();
    const btnFont = width < 420 ? styles.addBtnCompact : null;
    return (
        <HStack style={styles.dtBar}>
            <Stepper qty={qty} onQtyUpdated={onQtyUpdated}/>
            <TouchableWithoutFeedback onPress={() => onAdd()}>
                <HStack style={[styles.addBtn, {backgroundColor: accent}]}>
                    <Text style={[styles.addBtnLabel, {color: onAccent}, btnFont]} numberOfLines={1}>Add to Cart</Text>
                    <Text style={[styles.addBtnTotal, {color: onAccent}, btnFont]} numberOfLines={1}>{money(total)}</Text>
                </HStack>
            </TouchableWithoutFeedback>
        </HStack>
    );
};

const UpdateButton: React.FC<{
    menu: MenuListData, onEdit: () => void, onRemove: () => void, qty: number, total: number,
    onQtyUpdated: (qty: number) => void
}> = ({menu, onEdit, onRemove, qty, total, onQtyUpdated}) => {
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();
    const {width} = useWindowDimensions();
    const btnFont = width < 420 ? styles.addBtnCompact : null;
    return (
        <VStack style={styles.dtBarWrap}>
            <HStack style={styles.dtBar}>
                <Stepper qty={qty} onQtyUpdated={onQtyUpdated}/>
                <TouchableWithoutFeedback onPress={() => onEdit()}>
                    <HStack style={[styles.addBtn, {backgroundColor: accent}]}>
                        <Text style={[styles.addBtnLabel, {color: onAccent}, btnFont]} numberOfLines={1}>Update Cart</Text>
                        <Text style={[styles.addBtnTotal, {color: onAccent}, btnFont]} numberOfLines={1}>{money(total)}</Text>
                    </HStack>
                </TouchableWithoutFeedback>
            </HStack>
            <TouchableWithoutFeedback onPress={() => onRemove()}>
                <View style={styles.removeBtn}>
                    <Text style={styles.removeBtnText}>Remove from Cart</Text>
                </View>
            </TouchableWithoutFeedback>
        </VStack>
    );
};

function Menu({route}) {
    const [cart, setCart] = useRecoilState(cartState);
    const setLastCartTouch = useSetRecoilState(lastCartTouchState);   // 제출 시점 stale 확인(①)용 장바구니 변경 시각
    const happyhour: Happyhour = apps.getHappyhour();
    const saleMode: SaleMode = happyhour.saleMode;

    /** happyhour salemode가 shop을 시작할 때와 달라졌으면 팝업 안내를 표시하고 다시 처음부터 주문을 하게 한다. **/
    if ((apps.saleMode == SaleMode.HAPPY1 || apps.saleMode == SaleMode.HAPPY2) &&
        saleMode == SaleMode.NORMAL) {

        const happyhourDescr = apps.saleMode == SaleMode.HAPPY1 ? apps.currentShop.happy1Descr : apps.currentShop.happy2Descr;
        alert(`${happyhourDescr} has ended. Please reorder from the beginning` );

        location.reload();
    }

    const selected: CartItem = route.params == undefined ? null : cart.items.find(i => i.tag() == route.params.selectedTag);

    // console.log("selected: " + selected);

    const currentShop = apps.currentShop!!;
    const isMenuOnly = (currentShop?.tblNo || '').toLowerCase() === 'menu';  // ✅ 추가

    const menu = selected == null ? apps.selectedMenu!! : selected.menu;
    const navigation = useNavigation<any>();
    const { width } = useWindowDimensions();
    const wide = width >= 1100;   // 데스크탑 3-pane 여부(shop.tsx와 동일 기준)
    // 태블릿/데스크탑(≥700)은 어두운 backdrop 위 중앙 카드(maxWidth 640)로 표시, 폰(<700)은 풀스크린.
    const card = width >= 700;
    const heroH = card ? 400 : 240;   // 카드 폭 640 기준 1.6:1 / 폰은 종전 240 유지
    const [isAdd, setIsAdd] = useState(false);  // add button click 했는지
    const [optionSelected, setOptionSelected] =
        useState<Map<OptionListData, OptionItemData[]>>(selected == null ? new Map() : selected.options); // 선택된 옵션
    const [total, setTotal] = useState<number>(selected == null ? apps.getPrice(saleMode, menu) : selected.itemPrice * selected.qty); // 총 가격
    const [qty, setQty] = useState<number>(selected == null ? 1 : selected.qty);    // 수량
    const [note, setNote] = useState<string>(selected == null ? "" : (selected.note ?? ""));  // 항목별 메모(선택)
    const showNoteInput = apps.currentShop?.enableItemNote === true;
    const { showHint, scrollToEnd, scrollProps } = useScrollMore();   // 상세 스크롤 "더 있음" 힌트

    // console.log(menu);

    const options = apps.toOptions(menu.optionIds);
    // console.log(options);

    useEffect(() => {
            if (isAdd) {
                //console.log(cart);
                // showMessage({ message: `cart count: ${cart.count}` });
                // 데스크탑 3-pane(wide)에서는 우측 Your Order 패널이 이미 카트를 보여주므로 Cart 모달을
                // 열지 않고 목록(Shop)으로 복귀한다. Cart transparentModal을 여는 것이 FAB가 딸려 뜨는
                // 트리거였음. 모바일은 기존대로 Cart 모달을 연다.
                if (wide) {
                    navigation.goBack();
                } else {
                    navigation.replace("Cart");
                }
            }
            return () => {
                setIsAdd(false);
            }
        }, [isAdd],
    );

    const onAdd = () => {
        Keyboard.dismiss();   // 노트 입력 키보드 닫아 카트가 줄어든 뷰포트에서 작게 열리지 않게
        if (isMenuOnly) {                           // ✅ 가드
            showMessage({ message: "This screen is MENU display only.", type: "warning", duration: 1200 });
            return;
        }

        // 아래 두 가지 작업(에러 메시지 출력과 에러가 있는지 체크해서 리턴)을 한번에 할 수 없을까
        const missingRequired = options.find(
            (o: OptionListData) => o.optional == false && optionSelected.get(o) == undefined
        );

        if (missingRequired) {
            showMessage({
                message: "Please select " + missingRequired.descr,
                type: "danger",
                duration: 1000
            });
            return;
        }
        if (options.find((o: OptionListData) => o.optional == false && optionSelected.get(o) == undefined) != undefined) return;

        // happyhour only check
        if ((saleMode != SaleMode.HAPPY1 && menu.happy1Only) ||
            (saleMode != SaleMode.HAPPY2 && menu.happy2Only)) {
            showMessage({
                message: `You can order this menu only at ${apps.getHappyhourDescr(menu.happy1Only ? SaleMode.HAPPY1 : SaleMode.HAPPY2)} ${menu.happy1Only ? apps.getHappy1Time() : apps.getHappy2Time()}`,
                type: "danger",
                duration: 2000
            });
            return;
        }
        //console.log("menu:", JSON.stringify(menu, null, 2));
        // min/max order check
        const minOrd = menu.minOrd ?? 0;
        const maxOrd = menu.maxOrd ?? 0;

        if (minOrd !== 0 || maxOrd !== 0) {

            if (minOrd > 0 && qty < minOrd) {
                showMessage({
                    message: `The minimum order allowed is ${minOrd}`,
                    type: "danger",
                    duration: 1000
                });
                return;
            }

            if (maxOrd > 0 && qty > maxOrd) {
                showMessage({
                    message: `The maximum order allowed is ${maxOrd}`,
                    type: "danger",
                    duration: 1000
                });
                return;
            }
        }

        setCart(cart.addItemWithShop(saleMode, menu, optionSelected, qty, apps.currentShop!!, note.trim()));
        setLastCartTouch(Date.now());
        setIsAdd(true);
    };

    const onEdit = () => {
        if (isMenuOnly) {                           // ✅ 가드
            showMessage({ message: "This screen is MENU display only.", type: "warning", duration: 1200 });
            return;
        }

        // 아래 두 가지 작업(에러 메시지 출력과 에러가 있는지 체크해서 리턴)을 한번에 할 수 없을까
        const missingRequired = options.find(
            (o: OptionListData) => o.optional == false && optionSelected.get(o) == undefined
        );

        if (missingRequired) {
            showMessage({
                message: "Please select " + missingRequired.descr,
                type: "danger",
                duration: 1000
            });
            return;
        }
        if (options.find((o: OptionListData) => o.optional == false && optionSelected.get(o) == undefined) != undefined) return;

        //console.log("menu:", JSON.stringify(menu, null, 2));
        // min/max order check
        const minOrd = menu.minOrd ?? 0;
        const maxOrd = menu.maxOrd ?? 0;

        if (minOrd !== 0 || maxOrd !== 0) {

            if (minOrd > 0 && qty < minOrd) {
                showMessage({
                    message: `The minimum order allowed is ${minOrd}`,
                    type: "danger",
                    duration: 1000
                });
                return;
            }

            if (maxOrd > 0 && qty > maxOrd) {
                showMessage({
                    message: `The maximum order allowed is ${maxOrd}`,
                    type: "danger",
                    duration: 1000
                });
                return;
            }
        }

        // updateItemWithShop은 (신규 옵션/노트로) 기존 라인을 재탐색하는데, 편집으로 노트/옵션이 달라지면
        // 못 찾아 원본 라인이 안 지워지고 새 라인만 추가되는 문제가 있다(옵션 변경 시부터 존재하던 동작이나,
        // 노트 편집이 이 기능의 정상 사용 흐름이라 여기서 명시적으로 원본(selected)을 지우고 새로 추가한다.
        setCart(cart.removeItem(selected.tag()).addItemWithShop(saleMode, menu, optionSelected, qty, apps.currentShop!!, note.trim()));
        setLastCartTouch(Date.now());
        setIsAdd(true);
    };

    const onRemove = () => {
        setCart(cart.removeItem(selected.tag()));

        apps.selectedMenu = null;
        navigation.goBack()
    }

    const updateOption = (option: OptionListData, optionItems: OptionItemData[]) => {
        if (optionItems.length == 0) {
            optionSelected.delete(option)
            setOptionSelected(optionSelected);
        } else {
            setOptionSelected(optionSelected.set(option, optionItems));
        }

        let total = apps.getPrice(saleMode, menu);
        optionSelected.forEach((optionItems, option) =>
            total += optionItems.reduce((acc, cur) => acc + cur.price, 0)
        );

        // × qty so the button preview matches onQtyUpdated (display-only; the committed
        // cart total is computed independently in addItemWithShop from qty + options).
        setTotal(total * qty);
    }

    const onQtyUpdated = (qty) => {
        setQty(qty);

        let total = apps.getPrice(saleMode, menu);
        optionSelected.forEach((optionItems, option) =>
            total += optionItems.reduce((acc, cur) => acc + cur.price, 0)
        );

        setTotal(total * qty);
    }

    const closeDetail = () => {
        apps.selectedMenu = null;
        navigation.goBack();
    };

    return (
        <View style={styles.root}>
            {/* 태블릿/데스크탑: 뒤 메뉴 리스트를 어둡게 덮는 backdrop(탭하면 닫힘). 폰은 카드가 전체를 채워 backdrop 미노출. */}
            {card && (
                <TouchableWithoutFeedback onPress={closeDetail}>
                    <View style={styles.backdrop}/>
                </TouchableWithoutFeedback>
            )}
            <View style={card ? styles.cardWide : styles.cardFull}>
                <View style={{flex: 1, width: "100%"}}>
                    <ScrollView {...scrollProps} style={{flex: 1, width: "100%"}}>
                        <MenuImage menuUri={menuUri(currentShop, menu)} isNew={menu.newItem} heroH={heroH} goBack={closeDetail}/>
                        <MenuBase saleMode={saleMode} menu={menu}/>
                        <OptionList options={options} optionsSelected={optionSelected} onUpdate={updateOption}/>
                    </ScrollView>
                    <ScrollMoreHint visible={showHint} onPress={scrollToEnd}/>
                </View>

                {!isMenuOnly && (
                    <View style={{width: "100%"}}>
                        {showNoteInput && <NoteInput note={note} onChangeNote={setNote}/>}
                        {selected == null ?
                            <AddButton menu={menu} onAdd={onAdd} total={total} qty={qty} onQtyUpdated={onQtyUpdated}/>
                            :
                            <UpdateButton menu={menu} onEdit={onEdit} onRemove={onRemove} qty={qty} total={total} onQtyUpdated={onQtyUpdated}/>
                        }
                    </View>
                )}
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    root: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
    },
    // 태블릿/데스크탑: 뒤 메뉴 리스트를 덮는 어두운 backdrop.
    backdrop: {
        position: "absolute",
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(20,24,32,0.45)",
    },
    // 폰: 상세가 화면을 꽉 채우는 풀스크린(backdrop 미노출).
    cardFull: {
        flex: 1,
        width: "100%",
        backgroundColor: PALETTE.SURFACE,
    },
    // 태블릿/데스크탑: backdrop 위 중앙 카드(maxWidth 640, 넘치면 내부 스크롤).
    cardWide: {
        width: "100%",
        maxWidth: 640,
        height: "95%",
        backgroundColor: PALETTE.SURFACE,
        borderRadius: 16,
        overflow: "hidden",
        shadowColor: "#000",
        shadowOpacity: 0.25,
        shadowRadius: 24,
        shadowOffset: { width: 0, height: 8 },
        elevation: 12,
    },
    // ---- hero ----
    hero: {
        height: 240,
        width: "100%",
        backgroundColor: PALETTE.THUMB_BG,
        justifyContent: "flex-start",
        alignItems: "flex-start",
    },
    // 히어로 이미지 위 back 버튼 위치만 지정(원형 칩·그림자는 공용 CIRCLE_BTN).
    backBtnPos: {
        position: "absolute", top: 14, left: 14,
    },
    // NEW 배지(우상단) — 리스트 코너 배지와 동일 초록, 상세용으로 약간 크게.
    heroNewBadge: {
        position: "absolute", top: 14, right: 14, zIndex: 2,
        paddingHorizontal: 10, paddingVertical: 5, borderRadius: 7,
        backgroundColor: "#16a34a",
    },
    heroNewText: {fontSize: 12, fontWeight: "800", letterSpacing: 0.4, color: "#fff"},
    // ---- header ----
    menuBaseContainer: {
        paddingHorizontal: 18, paddingTop: 16, paddingBottom: 4,
        backgroundColor: PALETTE.SURFACE, width: "100%",
    },
    flagRow: {flexWrap: "wrap", marginBottom: 8},
    flagChip: {paddingHorizontal: 9, paddingVertical: 3, borderRadius: 7},
    flagText: {fontSize: 12, fontWeight: "800", letterSpacing: 0.3},
    menuNameRow: {alignItems: "center"},
    menuPriceRow: {alignItems: "baseline", marginTop: 6},
    menuName: {fontSize: 21, fontWeight: "800", color: PALETTE.TEXT, letterSpacing: -0.1, flex: 1, lineHeight: 26},
    menuPrice: {fontSize: 18, fontWeight: "800", color: PALETTE.PRICE, letterSpacing: -0.2},
    menuDescr: {marginTop: 8, marginBottom: 4, fontSize: 13, color: PALETTE.MUTED, lineHeight: 20},
    // ---- legend ----
    legend: {
        marginHorizontal: 18, marginVertical: 12,
        paddingHorizontal: 12, paddingVertical: 10,
        borderWidth: 1, borderColor: PALETTE.BORDER, borderRadius: 10,
        backgroundColor: PALETTE.SURFACE,
    },
    legendTitle: {fontSize: 12, fontWeight: "700", color: PALETTE.TEXT},
    legendDescr: {fontSize: 11, color: PALETTE.MUTED, marginTop: 2},
    // ---- option group ----
    og: {borderTopWidth: 8, borderTopColor: PALETTE.SUBTLE, paddingTop: 6, paddingBottom: 2},
    ogHead: {
        alignItems: "center", justifyContent: "space-between",
        paddingHorizontal: 18, paddingTop: 14, paddingBottom: 6,
    },
    ogTitle: {fontSize: 15, fontWeight: "800", color: PALETTE.TEXT, flex: 1},
    reqPill: {
        paddingHorizontal: 9, paddingVertical: 3, borderRadius: PALETTE.RADIUS_PILL,
        backgroundColor: PALETTE.ACCENT,
    },
    reqPillText: {fontSize: 10.5, fontWeight: "800", color: PALETTE.ACCENT, letterSpacing: 0.4, textTransform: "uppercase"},
    reqPillOptional: {
        paddingHorizontal: 9, paddingVertical: 3, borderRadius: PALETTE.RADIUS_PILL,
        backgroundColor: PALETTE.CHIP_BG,
    },
    reqPillOptionalText: {fontSize: 10.5, fontWeight: "800", color: PALETTE.MUTED, letterSpacing: 0.4, textTransform: "uppercase"},
    // ---- option item ----
    opt: {
        alignItems: "center", paddingHorizontal: 18, paddingVertical: 12,
        borderTopWidth: 1, borderTopColor: PALETTE.BORDER,
    },
    mark: {
        width: 22, height: 22, borderWidth: 2, borderColor: PALETTE.BORDER,
        alignItems: "center", justifyContent: "center", marginRight: 12,
    },
    markRadio: {borderRadius: 11},
    markCheck: {borderRadius: 6},
    markGlyph: {fontSize: 12, fontWeight: "700", lineHeight: 14},
    optLabel: {flex: 1, fontSize: 14, color: PALETTE.TEXT},
    optPrice: {fontSize: 13, fontWeight: "600", color: PALETTE.MUTED},
    // ---- note (optional, gated by shop.enableItemNote) ----
    noteWrap: {
        width: "100%", backgroundColor: PALETTE.SURFACE,
        paddingHorizontal: 16, paddingTop: 12,
        borderTopWidth: 1, borderTopColor: PALETTE.BORDER,
    },
    noteLabel: {
        fontSize: 13, fontWeight: "700", color: PALETTE.MUTED, marginBottom: 6,
    },
    noteInput: {
        borderWidth: 0.5, borderColor: PALETTE.MUTED, borderRadius: PALETTE.RADIUS_SM,
        backgroundColor: PALETTE.SUBTLE, padding: 10, fontSize: 14, color: PALETTE.TEXT,
        minHeight: 52, textAlignVertical: "top",
    },
    // ---- bottom bar ----
    dtBarWrap: {width: "100%", backgroundColor: PALETTE.SURFACE},
    dtBar: {
        width: "100%", alignItems: "center",
        paddingHorizontal: 16, paddingVertical: 12,
        borderTopWidth: 1, borderTopColor: PALETTE.BORDER,
        backgroundColor: PALETTE.SURFACE,
    },
    stepper: {
        alignItems: "center",
        borderWidth: 1, borderColor: PALETTE.BORDER, borderRadius: PALETTE.RADIUS_MD,
        paddingHorizontal: 10, paddingVertical: 7, marginRight: 12,
    },
    stepBtn: {
        width: 32, height: 32, borderRadius: 9, backgroundColor: PALETTE.CHIP_BG,
        alignItems: "center", justifyContent: "center",
    },
    stepBtnGlyph: {fontSize: 19, color: PALETTE.TEXT, lineHeight: 22},
    stepQty: {fontWeight: "800", fontSize: 16, color: PALETTE.TEXT, minWidth: 18, textAlign: "center", marginHorizontal: 12},
    addBtn: {
        flex: 1, alignItems: "center", justifyContent: "space-between",
        flexWrap: "nowrap",
        paddingHorizontal: 18, paddingVertical: 15, borderRadius: PALETTE.RADIUS_MD,
        backgroundColor: PALETTE.ACCENT,
    },
    // 라벨/금액 모두 한 줄 전체 노출(잘림 금지). 좁은 폭에선 addBtnCompact로 폰트만 축소.
    addBtnLabel: {fontSize: 15, fontWeight: "800", color: "white", flexShrink: 0, marginRight: 8},
    addBtnTotal: {fontSize: 15, fontWeight: "800", color: "white", flexShrink: 0},
    // 모바일(폭 < 420) 축소 폰트 — "Add to Cart · $금액"이 한 줄에 온전히 들어가도록.
    addBtnCompact: {fontSize: 13},
    removeBtn: {
        marginHorizontal: 16, marginBottom: 12,
        paddingVertical: 12, borderRadius: PALETTE.RADIUS_MD,
        backgroundColor: PALETTE.CHIP_BG, alignItems: "center",
    },
    removeBtnText: {fontSize: 14, fontWeight: "700", color: PALETTE.MUTED},
    // ---- price (sale) ----
    // 세일 배지 — 리스트(shop.tsx Badge SALE)와 동일 룩(빨강 pill), 상세용으로 약간 크게.
    saleBadge: {
        backgroundColor: PALETTE.SALE,
        paddingHorizontal: 7,
        paddingVertical: 3,
        borderRadius: 6,
        marginRight: 8,
    },
    saleBadgeText: {
        fontSize: 11,
        fontWeight: "800",
        letterSpacing: 0.4,
        color: "#fff",
    },
    menuPriceStroked: {
        fontFamily: "NotoSans-Regular",
        color: PALETTE.MUTED,
        fontSize: 18,
        textDecorationLine: "line-through"
    },
    specialPrice: {
        marginLeft: 8,
        fontFamily: "NotoSans-Regular",
        fontWeight: "800",
        color: PALETTE.SALE,
        fontSize: 18,
    },
});

export default Menu;
